<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue';
import { useDisplay } from 'vuetify';
import type { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';

import { useLocale } from '../../composables/useLocale';
import useModal, { ModalTypes } from '../../composables/useModal';
import { useManageGroup } from '../../composables/useManageGroup';
import useToast from '../../composables/useToast';
import Modal from '../Modal.vue';
import DeleteUsersInGroupsStatus from './DeleteUsersInGroupsStatus.vue';

const { t } = useLocale();
const { toast } = useToast();
const { smAndDown } = useDisplay();
const manageGroup = useManageGroup();

const props = defineProps<{
  name: ModalTypes;
  group: CustomerMgmtTypes.GetGroupResponse;
}>();
const { isOpen, close, context } = useModal(props.name);
const emits = defineEmits<{
  (event: 'groupDeleted', value: string): void;
}>();

const isDeleting = ref(false);
const deleteStudents = ref(false);

// step 1: Confirm deleting the group, optionally deleting the student accounts too,
//  step 2: Confirm deleting the student accounts, only reached when that was opted into

enum Step {
  CONFIRM_DELETION = 'confirmDeletion',
  CONFIRM_STUDENT_DELETION = 'confirmStudentDeletion',
}
const step = ref<Step>(Step.CONFIRM_DELETION);
const setStep = (newStep: Step) => {
  step.value = newStep;
};
const isStep = (checkStep: Step) => {
  return step.value === checkStep;
};

const deleteUsersInGroupsStatus = ref<InstanceType<typeof DeleteUsersInGroupsStatus> | null>(null);

const reset = () => {
  isDeleting.value = false;
  deleteStudents.value = false;
  setStep(Step.CONFIRM_DELETION);
};

watchEffect(() => {
  if (isOpen.value) {
    reset();
  }
});

const isCheckingUsers = computed(() => deleteUsersInGroupsStatus.value?.isPreparing === true);
const hasUsersToDelete = computed(() => deleteUsersInGroupsStatus.value?.hasUsersToDelete === true);
const hasNoUsersToDelete = computed(
  () => deleteUsersInGroupsStatus.value?.hasCheckCompleted === true && !hasUsersToDelete.value,
);

const deleteGroup = async () => {
  if (!props.group?.uid) {
    return;
  }

  isDeleting.value = true;

  let isGroupDeleted = false;
  if (deleteStudents.value === false) {
    isGroupDeleted = await manageGroup.deleteGroup(props.group.uid, props.group.schoolUnit?.uid);
  }
  if (deleteStudents.value === true && props.group.schoolUnit) {
    isGroupDeleted = await manageGroup.deleteGroupWithStudents(props.group.uid, props.group.schoolUnit.uid);
  }

  isDeleting.value = false;

  if (!isGroupDeleted) {
    toast(t('lcp.failed_to_delete_group'), 'error');
    return;
  }

  toast(t('lcp.group_deleted_successfully'), 'success');
  emits('groupDeleted', props.group.uid);
  close();
};

// Deleting the student accounts as well is destructive enough to be worth confirming on its own.
const confirmStudentDeletion = async () => {
  if (deleteStudents.value && hasUsersToDelete.value) {
    setStep(Step.CONFIRM_STUDENT_DELETION);
    return;
  }

  await deleteGroup();
};

const onBack = () => {
  const back = context.value.onBack;
  close();
  back?.();
};

const action = computed(() => {
  if (isStep(Step.CONFIRM_STUDENT_DELETION)) {
    return deleteGroup;
  }

  return confirmStudentDeletion;
});
const actionLabel = computed(() => {
  if (isStep(Step.CONFIRM_STUDENT_DELETION)) {
    return t('lcp.confirm_delete');
  }

  return t('lcp.delete');
});
const cancelAction = computed(() => {
  if (isStep(Step.CONFIRM_STUDENT_DELETION)) {
    return () => setStep(Step.CONFIRM_DELETION);
  }

  return onBack;
});
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.confirm_group_deletion')"
    :is-open="isOpen"
    :show-close-btn="!smAndDown"
    :top-left-btn-action="smAndDown ? onBack : undefined"
    top-left-btn-icon="mdi-chevron-left"
    width="560"
    :action="action"
    :action-label="actionLabel"
    :action-disabled="isDeleting"
    action-color="error"
    :loading="isDeleting || deleteUsersInGroupsStatus?.isPreparing"
    :cancel-action="cancelAction"
    :cancel-action-label="t('lcp.back')"
    cancel-action-color="success"
    :center-actions="smAndDown"
    @close="reset"
  >
    <v-row>
      <v-col cols="12">
        <p v-show="step === Step.CONFIRM_DELETION">
          {{ t('lcp.confirm_group_deletion_message') }}
        </p>
      </v-col>
      <v-divider />
      <template v-if="step === Step.CONFIRM_DELETION">
        <v-col cols="12">
          <v-checkbox
            v-model="deleteStudents"
            :label="t('lcp.delete_students_in_group')"
            color="primary"
            :disabled="isCheckingUsers || hasNoUsersToDelete"
            hide-details
          />
          <p v-if="hasNoUsersToDelete" class="pt-2 text-caption">
            {{ t('lcp.no_students_to_delete_in_group') }}
          </p>
        </v-col>
        <v-col cols="12">
          <p v-show="!deleteStudents">
            {{ t('lcp.confirm_group_deletion_no_delete_students_message') }}
          </p>
          <p v-show="deleteStudents">
            {{ t('lcp.confirm_group_deletion_yes_delete_students_message') }}
          </p>
        </v-col>
      </template>
      <v-col v-if="step === Step.CONFIRM_STUDENT_DELETION" cols="12">
        <p>
          {{ t('lcp.confirm_group_deletion_final_accounts_message') }}
        </p>
        <p class="pt-4">
          {{ t('lcp.confirm_group_deletion_final_data_message') }}
        </p>
        <p class="pt-4">
          {{ t('lcp.confirm_group_deletion_final_undone_message') }}
        </p>
      </v-col>
      <v-col v-show="deleteStudents" cols="12">
        <div class="pt-4" />
        <v-divider class="py-2" />
        <DeleteUsersInGroupsStatus
          ref="deleteUsersInGroupsStatus"
          :visible="isOpen"
          :selected-group-uids="[group.uid]"
        />
      </v-col>
    </v-row>
  </Modal>
</template>
